import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { ChatType } from '../../../shared/types/monitor/ChatType';
import { domainsPerModelOptions } from '../../../shared/helpers/MonitorTypeHelpers';
import { getDomainsStats } from '../../../shared/services/monitor/data/sources/MonitorDataSourcesService';
import { MonitorType } from '../../../shared/types/monitor/MonitorType';

export interface ModelDomainsData {
  chatType: ChatType;
  name: string;
  icon: string;
  totalCount: number;
  domains: { domain: string; count: number }[];
}

interface UseDomainsPerModelParams {
  projectId: number;
  startDate: Date | null;
  endDate: Date | null;
  features: MonitorType[];
  selectedChatType: ChatType | null;
  monitorPromptIds?: number[];
  selectedChatTypes?: ChatType[];
  filterMonitorTypes?: string[];
}

interface UseDomainsPerModelResult {
  modelsData: ModelDomainsData[];
  allDomains: string[];
  isLoading: boolean;
}

export const useDomainsPerModel = ({
  projectId,
  startDate,
  endDate,
  features,
  selectedChatType,
  monitorPromptIds,
  selectedChatTypes,
  filterMonitorTypes,
}: UseDomainsPerModelParams): UseDomainsPerModelResult => {
  const enabledModels = useMemo(
    () => {
      const all = domainsPerModelOptions(features);
      if (selectedChatType && selectedChatType !== 'ALL') {
        return all.filter((opt) => opt.type === selectedChatType);
      }
      if (selectedChatTypes && selectedChatTypes.length > 0 && selectedChatTypes.length < all.length) {
        return all.filter((opt) => selectedChatTypes.includes(opt.type));
      }
      return all;
    },
    [features, selectedChatType, selectedChatTypes]
  );

  const fromTimestamp = startDate?.getTime() ?? null;
  const toTimestamp = endDate?.getTime() ?? null;
  const queryEnabled = !!projectId && !!startDate && !!endDate;

  const queries = useQueries({
    queries: enabledModels.map((model) => ({
      queryKey: [
        'domains-per-model-stats',
        projectId,
        model.type,
        fromTimestamp,
        toTimestamp,
        monitorPromptIds,
      ],
      queryFn: () =>
        getDomainsStats(projectId, false, model.type, startDate, endDate, 1, 10, undefined, undefined, monitorPromptIds),
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: queryEnabled,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);

  const modelsData = useMemo<ModelDomainsData[]>(() => {
    return enabledModels.map((model, index) => {
      const result = queries[index];
      const response = result?.data;
      const domains =
        response?.domains?.map((d) => ({ domain: d.domain, count: d.count })) ?? [];
      const totalCount = domains.reduce((sum, d) => sum + d.count, 0);

      return {
        chatType: model.type,
        name: model.name,
        icon: model.icon ?? '',
        totalCount,
        domains,
      };
    });
  }, [enabledModels, queries]);

  const allDomainsQuery = useQuery({
    queryKey: ['domains-per-model-all-domains', projectId, fromTimestamp, toTimestamp, monitorPromptIds, filterMonitorTypes],
    queryFn: () => getDomainsStats(projectId, false, 'ALL', startDate, endDate, 1, 100, undefined, undefined, monitorPromptIds, filterMonitorTypes),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: queryEnabled,
  });

  const allDomains = useMemo<string[]>(() => {
    return allDomainsQuery.data?.domains?.map((d) => d.domain) ?? [];
  }, [allDomainsQuery.data]);

  return { modelsData, allDomains, isLoading };
};
